import { useEffect, useSyncExternalStore } from "react";

/**
 * Minimal module-level store for state that must be shared across components
 * which don't share a React context — e.g. a drawer whose trigger renders
 * outside the provider its content lives in, so a context can't bridge them.
 *
 * `useValue({ resetOnUnmount: true })` subscribes and, when the consumer
 * unmounts, clears the store back to its initial value — so a global open-flag
 * doesn't leak across navigations without each consumer hand-writing a cleanup
 * effect.
 */
export function createExternalStore<T>(initial: T) {
  let state = initial;
  const listeners = new Set<() => void>();

  const get = () => state;
  const set = (next: T) => {
    if (Object.is(state, next)) return;
    state = next;
    for (const listener of listeners) listener();
  };
  const subscribe = (onChange: () => void) => {
    listeners.add(onChange);
    return () => void listeners.delete(onChange);
  };

  function useValue(options?: { resetOnUnmount?: boolean }): T {
    // `get` doubles as the server snapshot: the store is module-level in-memory
    // state that starts at `initial` on the server and at first client render,
    // so both snapshots agree. Without it, any consumer rendered through
    // renderToStaticMarkup/SSR (e.g. QueueDrawer) throws "Missing
    // getServerSnapshot".
    const value = useSyncExternalStore(subscribe, get, get);
    const resetOnUnmount = options?.resetOnUnmount ?? false;
    useEffect(() => {
      if (!resetOnUnmount) return;
      return () => set(initial);
    }, [resetOnUnmount]);
    return value;
  }

  return { get, set, useValue };
}
